import type {
  InjectionProtocol,
  ProtocolBrand,
  ProtocolSite,
  ProtocolStep,
  ProtocolWarning,
} from "../types";

/**
 * Follitropin alfa with lutropin alfa — two hormones in one injection, and
 * the first medication here sold both as a pen to dial and as a kit to mix.
 *
 * Every walk before this was one device per trade name, or two devices that
 * are both ready to inject. Pergoveris is a multidose pen in three sizes
 * *and* a powder-plus-solvent vial, and the two are not variations of one
 * procedure: the pen is warmed, primed to a droplet and dialled in IU of
 * follitropin alfa; the vial is drawn up through one needle and injected
 * through another, and the label says where she may inject differs — the
 * pen's leaflet names the abdomen, the vial's names the abdomen or the front
 * of the thigh. So they are two protocols the way Ovidrel's are, and the
 * device question gains its first "a powder vial to mix" answer.
 *
 * ### What is specific to this medication
 *
 * - **The number in the window is the follitropin alfa dose.** The lutropin
 *   comes with it in a fixed 2:1 ratio and is never dialled. The pen's label
 *   says so; the walk says so at the dial.
 * - **First injection under supervision**, both presentations.
 * - **The pen's own split dose**: the window after the injection shows what
 *   an empty pen did not deliver, and the label's answer is a new pen for
 *   that number. Same shape as Gonal-F's, carried as a `verify` step.
 * - **The vial is for immediate, single use** — "may not be stored once
 *   opened and reconstituted" — and anything left is discarded.
 *
 * ### Market
 *
 * EMA-authorised, not an FDA product. Read in English and French; see
 * `docs/features/injection-coach/medications/pergoveris.md`. The content was
 * rewritten against the leaflets first: it carried one blended procedure for
 * both devices, a gauge no label states, "several seconds" where the pen's
 * label holds for at least five, and a missed-dose window no label has.
 *
 * Luveris (lutropin alfa alone) shares this content block and is **not** a
 * brand of this protocol: it is a different product with its own procedure.
 */

const PERGOVERIS_BRANDS = [
  {
    name: "Pergoveris",
    markets: ["EU", "GB", "CH", "AU", "NZ", "CA", "SG"],
  },
] as const satisfies readonly ProtocolBrand[];

const FIRST_INJECTION: ProtocolWarning = {
  id: "pergoveris.first-injection-under-supervision",
  severity: "caution",
  escalation: "none",
  copy: {
    en: "First injection - Your doctor or nurse will show you how to inject and will supervise your first injection; inject yourself at home only once they are satisfied you can do it safely.",
    fr: "Première injection - Votre médecin ou votre infirmier/ère vous montrera comment injecter et surveillera votre première injection ; faites-vous les injections à domicile seulement lorsqu'ils estiment que vous pouvez le faire sans danger.",
  },
  sourceSegment:
    "⚠ First injection - Your doctor or nurse will show you how to inject and will supervise your first injection; inject yourself at home only once they are satisfied you can do it safely.",
};

/** The leaflet's own shape: no double dose, contact the doctor. No hour. */
const MISSED_DOSE: ProtocolWarning = {
  id: "pergoveris.missed-dose",
  severity: "caution",
  escalation: "none",
  copy: {
    en: "Missed dose - Do not use a double dose to make up for a forgotten dose. Contact your clinic.",
    fr: "Dose oubliée - N'utilisez pas de dose double pour compenser une dose oubliée. Contactez votre centre.",
  },
  sourceSegment:
    "⚠ Missed dose - Do not use a double dose to make up for a forgotten dose. Contact your clinic.",
};

/**
 * What an empty pen does mid-injection: the window says what is missing and
 * the label's answer is a new pen for that number, not a call to wait for.
 */
const EMPTY_PEN: ProtocolWarning = {
  id: "pergoveris.pen.empty-pen",
  severity: "caution",
  escalation: "none",
  copy: {
    en: "Empty pen - If the window shows a number higher than 0 after your injection, that is the amount you still need; inject it with a new pen. If you are unsure how much you received, call your clinic.",
    fr: "Stylo vide - Si la fenêtre affiche un chiffre supérieur à 0 après l'injection, c'est la quantité qu'il vous reste à recevoir ; injectez-la avec un nouveau stylo. Si vous n'êtes pas sûre de la quantité reçue, appelez votre centre.",
  },
  sourceSegment:
    "⚠ Empty pen - If the window shows a number higher than 0 after your injection, that is the amount you still need; inject it with a new pen. If you are unsure how much you received, call your clinic.",
};

/**
 * OHSS to the clinic, in the leaflet's words ("contact your doctor straight
 * away… your doctor will need to examine you as soon as possible"), minus
 * difficulty breathing, which goes to emergency below.
 */
const OHSS: ProtocolWarning = {
  id: "pergoveris.ovarian-hyperstimulation",
  severity: "stop",
  escalation: "clinic-now",
  copy: {
    en: "Ovarian hyperstimulation - Contact your clinic straight away for lower abdominal pain together with nausea or vomiting, decreased urine production or weight gain; your doctor needs to examine you as soon as possible.",
    fr: "Hyperstimulation ovarienne - Contactez votre centre immédiatement en cas de douleurs dans le bas du ventre associées à des nausées ou des vomissements, de réduction de la production d'urine ou de prise de poids ; le médecin devra vous examiner dès que possible.",
  },
  sourceSegment:
    "⚠ Ovarian hyperstimulation - Contact your clinic straight away for lower abdominal pain together with nausea or vomiting, decreased urine production or weight gain; your doctor needs to examine you as soon as possible.",
};

/** The leaflet's allergic-reaction picture, routed to emergency. */
const BREATHING_OR_ALLERGIC: ProtocolWarning = {
  id: "pergoveris.breathing-or-allergic-reaction",
  severity: "stop",
  escalation: "emergency",
  copy: {
    en: "Seek urgent help - Difficulty breathing, swelling of your face, hives or a rash need emergency services straight away.",
    fr: "Aide urgente - Des difficultés à respirer, un gonflement du visage, une urticaire ou une éruption cutanée nécessitent d'appeler les secours tout de suite.",
  },
  sourceSegment:
    "⚠ Seek urgent help - Difficulty breathing, swelling of your face, hives or a rash need emergency services straight away.",
};

const PEN_STEPS: ProtocolStep[] = [
  {
    id: "pergoveris.pen.prepare",
    kind: "prepare",
    requiresConfirmation: false,
    copy: {
      en: "Prepare - Let the pen sit at room temperature for at least 30 minutes before use; do not use a microwave or other heating element to warm it. Wash your hands with soap and water and dry them well. Check that the name on the pen says {brand} and check the expiry date on the pen label. Pull off the pen cap and check that the medicine is clear, colourless and does not contain particles, and that the dose feedback window reads 0.",
      fr: "Préparer - Laissez le stylo à température ambiante pendant au moins 30 minutes avant utilisation ; n'utilisez pas de micro-ondes ni d'autre système chauffant pour le réchauffer. Lavez-vous les mains avec de l'eau et du savon et séchez-les bien. Vérifiez que le nom indiqué sur le stylo est bien {brand} et vérifiez la date de péremption sur l'étiquette du stylo. Retirez le capuchon du stylo et vérifiez que le médicament est limpide, incolore et sans particules, et que la fenêtre de confirmation de la dose affiche bien 0.",
    },
    warnings: [FIRST_INJECTION, MISSED_DOSE],
  },
  {
    id: "pergoveris.pen.attach-the-needle",
    kind: "prepare",
    requiresConfirmation: false,
    copy: {
      en: "Attach a new needle - Only use the single-use needles supplied, and a new one for each injection. Check that the outer needle cap and its peel-off seal are not damaged, then remove the seal. Screw the outer needle cap onto the threaded tip of the pen until you feel a light resistance; do not attach it too tightly. Pull the outer needle cap off gently and put it aside, you will need it to remove the needle. Hold the pen with the needle pointing upward and carefully remove and discard the inner needle shield.",
      fr: "Fixer une aiguille neuve - N'utilisez que les aiguilles à usage unique fournies, et une aiguille neuve à chaque injection. Vérifiez que le capuchon extérieur de l'aiguille et sa languette de protection ne sont pas endommagés, puis enlevez la languette. Vissez le capuchon extérieur de l'aiguille sur l'embout fileté du stylo jusqu'à sentir une légère résistance ; ne serrez pas trop fort. Retirez le capuchon extérieur en le tirant doucement et mettez-le de côté, vous en aurez besoin pour retirer l'aiguille. Tenez le stylo aiguille vers le haut et retirez délicatement le capuchon intérieur, puis jetez-le.",
    },
  },
  {
    // The label's own priming: dial to 25, needle up, tap, press. Confirmed
    // because the first injection of a new pen is silently short without it.
    id: "pergoveris.pen.check-for-a-droplet",
    kind: "verify",
    requiresConfirmation: true,
    copy: {
      en: "Check for a droplet - On a new pen only, look closely at the tip of the needle for a tiny droplet of liquid. If you see one, go on to set your dose. If you do not, turn the dose setting knob forward until it reads 25, hold the pen with the needle pointing upward, tap the reservoir holder gently and press the dose setting knob as far as it will go: a tiny droplet appears and the window reads 0. If no droplet appears, contact your clinic. On a pen you have already used, this check is not needed.",
      fr: "Vérifier la gouttelette - Sur un stylo neuf seulement, regardez attentivement l'extrémité de l'aiguille : une minuscule gouttelette de liquide doit s'y trouver. Si vous la voyez, passez au réglage de la dose. Sinon, tournez doucement le bouton de sélection de la dose vers l'avant jusqu'à ce que 25 s'affiche dans la fenêtre, tenez le stylo aiguille vers le haut, tapotez doucement le réservoir et poussez le bouton de sélection aussi loin que possible : une minuscule gouttelette se forme et la fenêtre affiche 0. Si aucune gouttelette n'apparaît, contactez votre centre. Sur un stylo déjà utilisé, cette vérification n'est pas nécessaire.",
    },
  },
  {
    id: "pergoveris.pen.ready-the-pen",
    kind: "verify",
    requiresConfirmation: true,
    copy: {
      en: "Set your dose - Turn the dose setting knob until your intended dose{dose} shows in the dose feedback window; you can turn it backwards if you go past it. The number in the window is the dose of follitropin alfa. Check that the window displays your complete prescribed dose before you move on.",
      fr: "Régler la dose - Tournez le bouton de sélection de la dose jusqu'à ce que la dose prévue{dose} s'affiche dans la fenêtre de confirmation de la dose ; vous pouvez le tourner vers l'arrière si vous l'avez dépassée. Le chiffre affiché est la dose de follitropine alfa. Vérifiez que la fenêtre affiche bien la dose complète qui vous a été prescrite avant de passer à la suite.",
    },
  },
  {
    id: "pergoveris.pen.clean-site",
    kind: "prepare",
    requiresConfirmation: false,
    copy: {
      en: "Clean the site - Clean the skin at an injection site on your abdomen with an alcohol swab, choosing a different site each day. Do not touch or cover the cleaned skin.",
      fr: "Désinfecter le site - Nettoyez la peau au niveau d'un site d'injection sur le ventre avec une compresse imbibée d'alcool, en changeant de site tous les jours. Ne touchez pas la peau nettoyée et ne la recouvrez pas.",
    },
  },
  {
    // No angle in the pen's label — "slowly push the needle into the skin
    // entirely" — so none is said and the figure draws none.
    id: "pergoveris.pen.inject",
    kind: "irreversible",
    requiresConfirmation: true,
    copy: {
      en: "Inject - Slowly push the needle into the skin entirely. Place your thumb in the middle of the dose setting knob, slowly press it down as far as it will go and hold it to complete the full injection. Keep it held for at least 5 seconds; the number in the window turns back to 0. Then pull the needle out of the skin while still holding the knob down, and release it only once the needle is out.",
      fr: "Injecter - Insérez lentement l'intégralité de l'aiguille dans la peau. Placez votre pouce au centre du bouton de sélection de la dose, appuyez lentement à fond et maintenez-le enfoncé pour effectuer l'injection complète. Maintenez-le enfoncé pendant au moins 5 secondes ; le chiffre affiché dans la fenêtre revient à 0. Retirez ensuite l'aiguille de la peau tout en maintenant le bouton enfoncé, et ne le relâchez qu'une fois l'aiguille sortie.",
    },
    warnings: [BREATHING_OR_ALLERGIC],
  },
  {
    id: "pergoveris.pen.inspect-dose-window",
    kind: "verify",
    requiresConfirmation: true,
    copy: {
      en: "Check the dose window - Check that the dose feedback window shows 0: then you have completed your dose. If it shows a number higher than 0, the pen is empty and you have not received your full dose; that number is the amount still to inject with a new pen, repeating all the steps.",
      fr: "Vérifier la fenêtre de dose - Vérifiez que la fenêtre de confirmation de la dose affiche bien 0 : vous avez alors reçu la dose complète. Si elle affiche un chiffre supérieur à 0, le stylo est vide et vous n'avez pas reçu la totalité de la dose ; ce chiffre est la quantité qu'il vous reste à injecter avec un nouveau stylo, en reprenant toutes les étapes.",
    },
    warnings: [EMPTY_PEN],
  },
  {
    id: "pergoveris.pen.aftercare",
    kind: "aftercare",
    requiresConfirmation: false,
    copy: {
      en: "After injection - Place the outer needle cap on a flat surface, slip the needle into it and push until you hear a click. Unscrew the needle and dispose of it in a sharps container. Put the pen cap back on and store the pen in its original packaging, never with the needle attached. Once opened, the pen keeps for a maximum of 28 days at 25 degrees Celsius.",
      fr: "Après l'injection - Posez le capuchon extérieur de l'aiguille sur une surface plane, faites-y glisser l'aiguille et pressez jusqu'à entendre un clic. Dévissez l'aiguille et jetez-la dans un collecteur d'aiguilles. Remettez le capuchon du stylo et rangez le stylo dans son emballage d'origine, jamais avec l'aiguille fixée. Une fois entamé, le stylo se conserve au maximum 28 jours à 25 degrés Celsius.",
    },
    warnings: [OHSS],
  },
];

/**
 * The vial's leaflet names two sites — "e.g. tummy, front of thigh" — so the
 * walk asks, as ganirelix does. The pen's leaflet names the abdomen only.
 */
const VIAL_SITES: readonly ProtocolSite[] = [
  {
    id: "abdomen",
    label: { en: "Abdomen", fr: "Ventre" },
    copy: {
      en: "Clean the site - Inject the solution immediately after mixing. Clean the chosen skin area on your abdomen with an alcohol swab using a circular motion, choosing a different site each day.",
      fr: "Désinfecter le site - Injectez la solution immédiatement après le mélange. Nettoyez la zone choisie sur le ventre avec un coton imbibé d'alcool en décrivant des mouvements circulaires, en changeant de site chaque jour.",
    },
  },
  {
    id: "thigh",
    label: { en: "Front of thigh", fr: "Dessus de la cuisse" },
    copy: {
      en: "Clean the site - Inject the solution immediately after mixing. Clean the chosen skin area on the front of your thigh with an alcohol swab using a circular motion, choosing a different site each day.",
      fr: "Désinfecter le site - Injectez la solution immédiatement après le mélange. Nettoyez la zone choisie sur le dessus de la cuisse avec un coton imbibé d'alcool en décrivant des mouvements circulaires, en changeant de site chaque jour.",
    },
  },
];

const VIAL_STEPS: ProtocolStep[] = [
  {
    id: "pergoveris.vial.prepare",
    kind: "prepare",
    requiresConfirmation: false,
    copy: {
      en: "Prepare - Wash your hands and find a clean area, such as a clean table. Lay out one vial of {brand} powder, one vial of water for injections (the solvent), two alcohol swabs, an empty syringe, a needle for preparation, a fine needle for injection under the skin, and a sharps container. Give yourself the injection at the same time each day.",
      fr: "Préparer - Lavez-vous les mains et trouvez une surface propre, comme une table propre. Disposez un flacon de poudre de {brand}, un flacon d'eau pour préparations injectables (le solvant), deux cotons imbibés d'alcool, une seringue vide, une aiguille pour la préparation, une aiguille fine pour l'injection sous la peau et un collecteur. Faites-vous l'injection à la même heure chaque jour.",
    },
    warnings: [FIRST_INJECTION, MISSED_DOSE],
  },
  {
    id: "pergoveris.vial.draw-up-the-solvent",
    kind: "prepare",
    requiresConfirmation: false,
    copy: {
      en: "Draw up the solvent - Remove the protective cap from the solvent vial. Attach the needle for preparation to the empty syringe, draw up air by pulling the plunger to approximately the 1 mL mark, insert the needle into the vial and push the plunger to expel the air. Turn the vial upside down and gently draw up all the water. Remove the syringe from the vial and set it down carefully; do not touch the needle and do not let it touch any surface.",
      fr: "Prélever le solvant - Enlevez l'opercule protecteur du flacon de solvant. Montez l'aiguille pour la préparation sur la seringue vide, aspirez de l'air en tirant le piston à peu près jusqu'à la graduation de 1 mL, introduisez l'aiguille dans le flacon et poussez le piston pour expulser l'air. Retournez le flacon la tête en bas et aspirez doucement toute l'eau. Retirez la seringue du flacon et déposez-la précautionneusement ; ne touchez pas l'aiguille et ne la laissez toucher aucune surface.",
    },
  },
  {
    // Confirmed: the one look that decides whether the vial is usable, and
    // the step a patient in a hurry skips.
    id: "pergoveris.vial.reconstitute",
    kind: "verify",
    requiresConfirmation: true,
    copy: {
      en: "Reconstitute - Remove the protective cap from the vial of {brand} powder. Slowly inject the contents of the syringe into the vial of powder. Swirl gently without removing the syringe; do not shake. After the powder has dissolved, check that the solution is clear and does not contain any particles. Turn the vial upside down and gently draw the solution back into the syringe; do not use it if the solution is not clear.",
      fr: "Reconstituer - Enlevez l'opercule protecteur du flacon de poudre de {brand}. Injectez lentement le contenu de la seringue dans le flacon de poudre. Remuez très doucement sans enlever la seringue ; n'agitez pas. Une fois la poudre dissoute, vérifiez que la solution est limpide et ne contient pas de particules. Retournez le flacon la tête en bas et aspirez doucement la solution dans la seringue ; ne l'injectez pas si elle n'est pas limpide.",
    },
  },
  {
    id: "pergoveris.vial.change-the-needle",
    kind: "verify",
    requiresConfirmation: true,
    copy: {
      en: "Change the needle - Replace the needle with the fine bore needle. If you see air bubbles in the syringe, hold it with the needle pointing upwards, gently flick it until all the air collects at the top, and push the plunger until the bubbles are gone.",
      fr: "Changer l'aiguille - Remplacez l'aiguille par l'aiguille fine pour l'injection. Si vous voyez des bulles d'air dans la seringue, tenez-la aiguille vers le haut, tapotez-la doucement pour faire remonter les bulles, et poussez le piston jusqu'à ce qu'elles aient disparu.",
    },
  },
  {
    id: "pergoveris.vial.clean-site",
    kind: "prepare",
    requiresConfirmation: false,
    copy: VIAL_SITES[0]!.copy,
  },
  {
    id: "pergoveris.vial.inject",
    kind: "irreversible",
    requiresConfirmation: true,
    copy: {
      en: "Inject - Firmly pinch the skin together and insert the needle at a 45° to 90° angle using a dart-like motion. Inject under the skin, as you were taught; do not inject directly into a vein. Inject the solution by pushing gently on the plunger, taking as much time as you need to inject all of it. Then withdraw the needle and clean the skin with a new alcohol swab using a circular motion.",
      fr: "Injecter - Pincez fermement la peau et enfoncez l'aiguille avec un angle de 45° à 90°, comme si vous enfonciez une fléchette. Injectez sous la peau, comme on vous l'a appris ; n'injectez jamais directement dans une veine. Injectez la solution en poussant doucement sur le piston, en prenant tout le temps nécessaire pour injecter toute la solution. Retirez ensuite l'aiguille et nettoyez la peau avec un nouveau coton imbibé d'alcool, en décrivant des mouvements circulaires.",
    },
    warnings: [BREATHING_OR_ALLERGIC],
  },
  {
    id: "pergoveris.vial.aftercare",
    kind: "aftercare",
    requiresConfirmation: false,
    copy: {
      en: "After injection - Once you have finished, immediately discard all needles and empty vials in your sharps container. Any unused solution must be discarded.",
      fr: "Après l'injection - Lorsque vous avez terminé, jetez immédiatement les aiguilles et les flacons vides dans votre collecteur. Toute solution non utilisée doit être jetée.",
    },
    warnings: [OHSS],
  },
];

const SHARED = {
  medicationSlug: "pergoveris",
  brands: PERGOVERIS_BRANDS,
  // The content once listed Luveris, lutropin alfa and r-hLH here too. None
  // of them is this medication: Luveris is lutropin alfa alone, with its own
  // kit and now its own entry and walk, and a prescription naming the LH
  // component is not a prescription for this one.
  aliases: ["Follitropin alfa lutropin alfa"],
  indication: "stimulation",
  // International units of follitropin alfa, titrated per patient. The pen
  // tops out at 300 or 450 per injection depending on the pen; the vial is
  // 150 IU per vial. No fixed list — the clinic adds follitropin alfa to it.
  dose: { unit: "IU" },
  route: "subcutaneous",
  version: "2026-10-05.1",
  derivedFrom: "content/data/medications/gonadotropins.json#pergoveris",
} as const satisfies Partial<InjectionProtocol>;

export const PERGOVERIS_PEN_PROTOCOL: InjectionProtocol = {
  ...SHARED,
  form: "pen",
  prescriptionForms: ["pre_filled_pen"],
  steps: PEN_STEPS,
};

export const PERGOVERIS_VIAL_PROTOCOL: InjectionProtocol = {
  ...SHARED,
  form: "vial",
  prescriptionForms: ["lyophilized_powder"],
  sites: VIAL_SITES,
  steps: VIAL_STEPS,
};

export const PERGOVERIS_PROTOCOLS: readonly InjectionProtocol[] = [
  PERGOVERIS_PEN_PROTOCOL,
  PERGOVERIS_VIAL_PROTOCOL,
];
